import React, { useEffect, useState } from "react";
import { ReactComponent as UserIcon } from "../../../../../../assets/svg/icons/user-no-fill.svg";
import DirectDebitMandateModalPrompt from "./DirectDebitMandateModalPrompt";
import CustomSelectDropdown from "../../../../../../NewComponents/CustomHTMLElements/CustomSelectDropdown";
import useDirectDebitListAccounts from "../../../../../../custom-hooks/useDirectDebitListAccounts";
import useDirectDebitMandateList from "../../../../../../custom-hooks/useDirectDebitMandateList";
import { formatMoney } from "accounting";
import useDirectDebitQueryStatus from "../../../../../../custom-hooks/useDirectDebitQueryStatus";
import { useQueryCache } from "react-query";
import { postData } from "../../../../../../newApis/apiMethods";
import * as ajaxEndpoints from "../../../../../../api/ajax-endpoints";
import { appInsights } from "../../../../../../config/appInsights";
import { errorHandler } from "../../../../../../helpers/errorHandler";
import { IState } from "../../interface";
import MandatePayload from "./MandatePayload";
import { statusProps } from "../LoanSchedule/type";
import Alert from "../../../../../../NewComponents/TypedComponents/Alert/Alert.component";
import { getData } from "../../../../../../newApis/dotNetApiMethods";
import { MixPanel } from "../../../../../../shared/MixPanel/MixPanel";
import {
  useFetchNibssMandateTypes,
  useFetchPaperMandateTemplate,
} from "../../../../../../custom-hooks/useFetchNibssMandateTypes";
import styles from "./DirectDebitStyles.module.scss";
import ViewMandateTemplate from "./ViewMandateTemplate";
import ButtonLoader from "../../../../../../UI/Loaders/Loaders";
import { Table, Dropdown, Menu } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EllipsisOutlined } from "@ant-design/icons";
import RejectMandate from "./RejectMandate";
import { splitAtLastOccurrenceOfItem } from "../../../../../../NewComponents/CustomViewDocumentHandler/helper";
import { getMimeType } from "../../../../../../helpers/convertToFormData";
import useDocumentViewer from "../../../../../../NewComponents/CustomViewDocumentHandler/CustomViewDocumentHandler";
import CustomerPaperMandate from "./ViewCustomerPaperMandate";
import ConfirmMandateApproval from "./ConfirmMandateApproval";

export interface IMandatePreviewDetails {
  loanId: number;
  reference: string;
  payerBank: string;
  payerAccountNumber: string;
  payerPhone: string;
  payerEmailAddress: string;
  payerAddress: string;
  receiverBank: string;
  receiverAccountNumber: string;
  amount: string;
  startDate: string;
  endDate: string;
  frequency: string;
  frequencyName: string;
  payerbankName: string;
  receiverBankName: string;
  loanRef: string;
  currency: string;
  directDebitProvider: string;
  payerBvn: string;
  payerName?: string;
}

export interface SelectedMandateItemProps {
  id: number;
  customer_other_bank_account_id: number;
  customer_bank_account_number: string;
  customer_bank_account_name: string;
  customer_bank_name: string;
  reference_code: string;
  loan_id: number;
  loan_ref: string;
  amount: string | number;
  direct_debit_mandate_status: string;
  customer_name: string;
  customer_email: string;
  direct_debit_request_payload?: any;
  direct_debit_response_payload?: any;
  currency: string;
  mandate_type_id: number;
  paper_mandate_file_path?: string | null;
  decline_comment?: string | null;
}

const DirectDebit = ({
  loan_ref,
  loan_id,
  first_name,
  last_name,
  properties,
  state,
}: {
  loan_ref: string;
  loan_id: number;
  first_name: string;
  last_name: string;
  state: IState;
  properties: any;
}) => {
  const [mandateQueryStatusRefCode, setmandateQueryStatusRefCode] =
    useState<any>();
  const [currentBankAccount, setCurrentBankAccount] = useState<string | null>(
    null,
  );
  const [success, setSuccess] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showMandateTemplateModal, setShowMandateTemplateModal] =
    useState(false);
  const [mandateTypeId, setMandateTypeId] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectReasonSuccessMsg, setRejectReasonSuccessMsg] = useState("");
  const [rejectReasonErrorMsg, setRejectReasonErrorMsg] = useState("");
  const [selectedMandateItem, setSelectedMandateItem] =
    useState<SelectedMandateItemProps | null>(null);
  const [showCustomerMandateModal, setShowCustomerMandateModal] =
    useState(false);
  const [mandatePreviewLoading, setMandatePreviewLoading] = useState(false);
  const [mandatePreviewError, setMandatePreviewError] = useState<string | null>(
    null,
  );
  const [mandatePreviewSuccess, setMandatePreviewSuccess] = useState<
    string | null
  >("");
  const [mandatePreviewResponseData, setMandatePreviewResponseData] =
    useState<IMandatePreviewDetails | null>(null);

  const [requestPayLoad, setRequestPayLoad] = useState<any>();
  const [responsePayLoad, setResponsePayLoad] = useState<any>();
  const [resendMandateStatus, setResendMandateStatus] = useState<statusProps>();
  const [warningMsg, setWarningMsg] = useState<string | null>("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFetchingDocument, setIsFetchingDocument] = useState(false);
  const [customerMandateUrl, setCustomerMandateUrl] = useState("");
  const [isLoadingApproval, setIsLoadingApproval] = useState<boolean>(false);
  const [approveMandateSuccessMsg, setApproveMandateSuccessMsg] = useState("");
  const [approveMandateErrorMsg, setApproveMandateErrorMsg] = useState("");
  const [showConfirmMandateApprovalModal, setShowConfirmMandateApprovalModal] =
    useState(false);

  const { downloadFileAndConvertToBase64 } = useDocumentViewer();

  const queryCache = useQueryCache();

  const showMandateTypeDropdown =
    properties?.dataRecord?.application_type?.toLowerCase()?.trim() === "spark";

  const isCreateMandateDisabled =
    properties?.dataRecord?.application_type?.toLowerCase()?.trim() === "spark"
      ? !currentBankAccount || !mandateTypeId
      : !currentBankAccount;

  useEffect(() => {
    MixPanel.track(`Page View: DirectDebit.tsx`, {
      page: "DirectDebit.tsx",
    });
  }, []);

  const customerId = properties?.dataRecord?.borrower_details?.customer_id;

  const {
    data: directDebitListAccountsData,
    isLoading: DirectDebitListAccountsLoading,
    error: DirectDebitListAccountsError,
  } = useDirectDebitListAccounts(customerId);

  const {
    data: directDebitMandateTableList,
    isLoading: directDebitMandateTableListLoading,
    error: directDebitMandateTableListError,
    refetch: refetchDirectDebitMandateList,
  } = useDirectDebitMandateList({
    account_number: currentBankAccount ? currentBankAccount : "",
    loan_ref,
  });

  const {
    data: directDebitQueryStatus,
    isLoading: directDebitQueryLoading,
    error: directDebitQueryError,
    refetch,
  } = useDirectDebitQueryStatus({
    mandate_reference_code: mandateQueryStatusRefCode,
    loan_ref,
  });

  const { data: mandateTypes, isLoading } = useFetchNibssMandateTypes();
  const { data: mandateTemplate, isLoading: isLoadingMandateTemplate } =
    useFetchPaperMandateTemplate();

  const renderMandateTypeOptions = () => {
    const value = mandateTypes?.data?.mandateTypes?.map(
      (item: { id: number; name: string }) => (
        <option value={item.id}>{item.name}</option>
      ),
    );

    return value;
  };

  const handleRadioChange = (option: any) => {
    setCurrentBankAccount(option);

    if (option) {
      setWarningMsg(
        "Please ensure you confirm the repayment schedule before creating the mandate",
      );
    }
  };
  const columns: ColumnsType<SelectedMandateItemProps> = [
    {
      title: "Bank",
      dataIndex: "customer_bank_account_name",
      key: "customer_bank_account_name",
      align: "center",
    },
    {
      title: "Account Number",
      dataIndex: "customer_bank_account_number",
      key: "customer_bank_account_number",
      align: "center",
    },
    {
      title: "Ref Code",
      dataIndex: "reference_code",
      key: "reference_code",
      align: "center",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value: any) => formatMoney(value, "NGN"),
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "direct_debit_mandate_status",
      key: "direct_debit_mandate_status",
      render: (value: any) => value || "N/A",
      align: "center",
    },
    {
      title: "Workflow Status",
      dataIndex: "direct_debit_mandate_status_name",
      key: "direct_debit_mandate_status_name",
      render: (value: any) => value || "N/A",
      align: "center",
    },
    {
      title: "Action Status",
      key: "actions",
      render: (_: any, record: any) => {
        const actionMenu = (
          <Menu
            items={[
              {
                key: "get-status",
                label: "Get Status",
                onClick: () => handleRefQuery(record.reference_code),
              },
              record.direct_debit_mandate_status === "Pending"
                ? {
                    key: "resend",
                    label: "Resend Mandate",
                    onClick: () =>
                      handleResendMandateNotification(record.reference_code),
                  }
                : null,
              // {
              //   key: "view-payload",
              //   label: "View Mandate Payload",
              //   onClick: () =>
              //     handlePayloadData(
              //       record.direct_debit_request_payload,
              //       record.direct_debit_response_payload,
              //     ),
              // },
              record.direct_debit_mandate_status?.toLowerCase() ===
              "pendingwithcredit"
                ? {
                    key: "view-mandate",
                    label: "View Mandate",
                    onClick: () => {
                      handleViewDocument(record?.paper_mandate_file_path);
                      setShowCustomerMandateModal(true);
                    },
                  }
                : null,
              record.direct_debit_mandate_status?.toLowerCase() ===
              "pendingwithcredit"
                ? {
                    key: "approve-mandate",
                    label: "Approve Mandate",
                    onClick: () => {
                      setCurrentBankAccount(
                        record.customer_bank_account_number,
                      );
                      setSelectedMandateItem(record);
                      setShowConfirmMandateApprovalModal(true);
                    },
                  }
                : null,
              record.direct_debit_mandate_status?.toLowerCase() ===
              "pendingwithcredit"
                ? {
                    key: "reject-mandate",
                    label: "Reject Mandate",
                    onClick: () => {
                      setSelectedMandateItem(record);
                      setShowRejectModal(true);
                    },
                  }
                : null,
              record?.decline_comment
                ? {
                    key: "view_comment",
                    label: "View Comment",
                    onClick: () => {
                      setSelectedMandateItem(record);
                      setShowRejectModal(true);
                    },
                  }
                : null,
            ].filter(Boolean)}
          />
        );

        return (
          <Dropdown overlay={actionMenu} trigger={["click"]}>
            <EllipsisOutlined className={styles.ellipsis_icon} />
          </Dropdown>
        );
      },
      align: "center",
    },
  ];

  // check the values create mandate repayment preview

  const createMandatePreview = async () => {
    setMandatePreviewLoading(true);
    setMandatePreviewError(null);
    setMandatePreviewSuccess("");
    const reqBody = {
      loanId: loan_id,
      customerBankAccountNumber: currentBankAccount,
      principalAmount: state.amountApproved,
      loanEffectiveDate: state.selectedDate,
    };
    if (state.amountApproved && state.selectedDate && loan_id) {
      try {
        const response = await postData(
          ajaxEndpoints.DIRECT_DEBIT_EMANDATE_PREVIEW,
          reqBody,
        );

        if (response.status) {
          setMandatePreviewResponseData(response.data);
          setMandatePreviewLoading(false);
          setMandatePreviewSuccess(response?.message);
          setCurrentBankAccount(null);

          setTimeout(() => {
            setMandatePreviewSuccess("");
          }, 3000);
        }
      } catch (error) {
        appInsights.trackException({
          //@ts-ignore
          exception: error,
          properties: { fileName: "DirectDebit.tsx" },
        });
        setMandatePreviewLoading(false);
        setMandatePreviewError(errorHandler(error));
      } finally {
        setMandatePreviewLoading(false);
      }
    }
  };

  useEffect(() => {
    if (mandateQueryStatusRefCode) {
      refetch();

      if (directDebitQueryStatus?.direct_debit_mandate_status) {
        queryCache.invalidateQueries([
          {
            loan_ref: loan_ref,
            account_number: currentBankAccount || "",
          },
          "getDirectDebitMandateList",
        ]);
      }
    }
  }, [
    mandateQueryStatusRefCode,
    directDebitQueryStatus,
    refetch,
    queryCache,
    loan_ref,
    currentBankAccount,
  ]);
  const handleRefQuery = (reference_code: string) => {
    setmandateQueryStatusRefCode(reference_code);
  };

  const handleDownload = async (isTemplate: boolean = true) => {
    const downloadUrl = isTemplate
      ? mandateTemplate?.data?.file_url + `.docx`
      : customerMandateUrl;

    const fileNameFromServer = isTemplate
      ? mandateTemplate?.data?.file_name
      : downloadUrl.split("/").pop();

    try {
      setIsDownloading(true);

      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      let extension = "";
      const mime = blob.type;

      if (mime.includes("pdf")) extension = "pdf";
      else if (mime.includes("png")) extension = "png";
      else if (mime.includes("jpeg")) extension = "jpg";
      else if (mime.includes("jpg")) extension = "jpg";
      else if (mime.includes("document")) extension = "docx";

      if (!extension && fileNameFromServer) {
        const parts = fileNameFromServer.split(".");
        extension = parts.pop()?.toLowerCase() || "docx";
      }

      const baseName = fileNameFromServer
        ? fileNameFromServer.replace(/\.[^/.]+$/, "")
        : "mandate";

      const finalFileName = `${baseName}.${extension}`;

      const link = document.createElement("a");
      link.href = url;
      link.download = finalFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const renderWithFilePath = async (filePath: string, mimeType: string) => {
    setIsFetchingDocument(true);
    try {
      const { data } = await getData(
        `${ajaxEndpoints.DOCUMENT_URL}?file_path=${filePath}`,
      );

      if (data?.data?.file_url) {
        downloadFileAndConvertToBase64(
          data?.data?.file_url,
          data?.data?.file_name,
          mimeType,
        );
        setCustomerMandateUrl(data?.data?.file_url + `.docx`);
      }
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "CustomViewDocumentHandler.ts",
        },
      });
    } finally {
      setIsFetchingDocument(false);
    }
  };

  const handleViewDocument = (url: string) => {
    const result = splitAtLastOccurrenceOfItem(url, ".");
    const mimeType = getMimeType(result);

    return renderWithFilePath(url, mimeType);
  };

  const handleResendMandateNotification = async (reference_code: string) => {
    setResendMandateStatus({ status: "loading", error: false });

    try {
      const response = await getData(
        `${ajaxEndpoints.RESEND_MANDATE_ACTIVATION}?loan_ref=${loan_ref}&mandate_reference_code=${reference_code}`,
      );

      if (response?.data?.status === true) {
        setResendMandateStatus({
          status: "success",
          error: false,
          message: response?.data?.message,
        });
      } else {
        setResendMandateStatus({
          status: "error",
          error: true,
        });
      }
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "DirectDebit.tsx",
        },
      });
      setResendMandateStatus({
        status: "error",
        error: true,
        message: error?.response?.data.message ?? error.message,
      });
    }
  };

  const handleMandateRejection = async () => {
    setRejectLoading(true);
    setRejectReasonErrorMsg("");

    const payload = {
      LoanRef: selectedMandateItem?.loan_ref,
      MandateReferenceCode: selectedMandateItem?.reference_code,
      MandateId: selectedMandateItem?.id,
      comment: rejectReason,
    };

    try {
      const response = await postData(ajaxEndpoints.DECLINE_PAPER_MANDATE, {
        ...payload,
      });

      if (response.status) {
        refetchDirectDebitMandateList();
        setRejectReasonSuccessMsg(response.message);
        setShowRejectModal(false);
        setSelectedMandateItem(null);

        setTimeout(() => {
          setRejectReasonSuccessMsg("");
        }, 3000);
      }
    } catch (error) {
      setRejectReasonErrorMsg(errorHandler(error));
    } finally {
      setRejectLoading(false);
    }
  };

  const onApproveMandate = async () => {
    setIsLoadingApproval(true);
    const reqBody = {
      loanId: loan_id,
      customerBankAccountNumber: currentBankAccount,
      principalAmount: state.amountApproved,
      loanEffectiveDate: state.selectedDate,
    };
    if (
      state.amountApproved &&
      state.selectedDate &&
      loan_id &&
      state.selectedDate
    ) {
      try {
        const response = await postData(
          ajaxEndpoints.DIRECT_DEBIT_EMANDATE_PREVIEW,
          reqBody,
        );

        if (response.status) {
          const approveRes = await postData(
            ajaxEndpoints.APPROVE_DIRECT_DEBIT_EMANDATE,
            {
              ...response.data,
              mandateId: Number(selectedMandateItem?.id),
              mandateTypeId: Number(selectedMandateItem?.mandate_type_id),
              paperMandateFilePath:
                selectedMandateItem?.paper_mandate_file_path,
            },
          );

          if (approveRes.status) {
            setApproveMandateSuccessMsg(approveRes.message);
            queryCache.invalidateQueries([
              {
                loan_ref: selectedMandateItem?.loan_ref,
                account_number:
                  selectedMandateItem?.customer_bank_account_number || "",
              },
              "getDirectDebitMandateList",
            ]);
            setCurrentBankAccount(null);
            setShowConfirmMandateApprovalModal(false);
          }

          setTimeout(() => {
            setApproveMandateSuccessMsg("");
          }, 3000);
        }
      } catch (error) {
        appInsights.trackException({
          //@ts-ignore
          exception: error,
          properties: { fileName: "DirectDebit.tsx" },
        });
        setApproveMandateErrorMsg(errorHandler(error));
      } finally {
        setIsLoadingApproval(false);
      }
    }
  };

  const handlePayloadData = (
    direct_debit_request_payload: any,
    direct_debit_response_payload: any,
  ) => {
    if (direct_debit_request_payload && direct_debit_response_payload) {
      setRequestPayLoad(direct_debit_request_payload);
      setResponsePayLoad(direct_debit_response_payload);
    }
  };
  const scrollToTop = () => {
    //@ts-ignore
    document.getElementById("topOfPage").scrollIntoView({
      behavior: "smooth",
    });
  };
  return (
    <div
      id="topOfPage"
      style={{
        padding: "1rem",
        height: "70vh",
        scrollbarColor: "auto",
        overflowY: "scroll",
      }}
    >
      {success && (
        <p className="alert alert-success text-align-center py-2">{success}</p>
      )}
      {approveMandateSuccessMsg && (
        <p className="alert alert-success text-align-center py-2">
          {approveMandateSuccessMsg}
        </p>
      )}
      {rejectReasonSuccessMsg && (
        <p className="alert alert-success text-align-center py-2">
          {rejectReasonSuccessMsg}
        </p>
      )}
      {error && (
        <p className="alert alert-danger text-align-center py-2">{error}</p>
      )}
      {directDebitMandateTableListError && (
        <p className="alert alert-danger text-align-center py-2">
          {directDebitMandateTableListError as string}
        </p>
      )}
      {directDebitQueryError && (
        <p className="alert alert-danger text-align-center py-2">
          {directDebitQueryError as string}
        </p>
      )}
      {DirectDebitListAccountsError && (
        <p className="alert alert-danger text-align-center py-2">
          {DirectDebitListAccountsError as string}
        </p>
      )}

      <div className={styles.headerContent}>
        <div>
          <p className="py-2">Please select Direct Debit Account </p>
          <div>
            <UserIcon />
            User: <span>{`${first_name}  ${last_name}`}</span>
          </div>
        </div>

        {showMandateTypeDropdown && mandateTypeId === "2" && (
          <div className={styles.paperMandateBtnContainer}>
            <button
              // type="button"
              // data-toggle="modal"
              // data-target="#viewMandateTemplate"
              onClick={() => setShowMandateTemplateModal(true)}
              className={`${styles.view_btn} btn zoom-element`}
              disabled={isLoadingMandateTemplate}
            >
              View Template
            </button>

            <button
              type="button"
              className={`${styles.download_btn} btn zoom-element`}
              onClick={() => handleDownload(true)}
              disabled={isDownloading}
            >
              {isDownloading ? <ButtonLoader /> : "Download Template"}
            </button>
          </div>
        )}
      </div>

      {warningMsg && <Alert type="warning" message={`${warningMsg}`} />}

      {showMandateTypeDropdown ? (
        <div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: "1.5rem",
              marginTop: "1.5rem",
            }}
          >
            <div style={{ flex: "1 1 48%" }}>
              <CustomSelectDropdown
                label="Select Direct Debit Account"
                extraLabel={DirectDebitListAccountsLoading ? "loading..." : ""}
                onChange={(e) => handleRadioChange(e.target.value)}
                name="directDebitAccounts"
                className="round user__form form-control"
              >
                <option value="">All Direct Debit Accounts</option>
                {directDebitListAccountsData?.bank_accounts?.map(
                  ({
                    account_number,
                    account_name,
                    bank_name,
                    is_default,
                  }: {
                    account_number: string;
                    account_name: string;
                    bank_name: string;
                    is_default: boolean;
                  }) => (
                    <option value={account_number} key={account_number}>
                      {`${bank_name} - ${account_number} - ${account_name} ${
                        is_default ? "(Selected by customer)" : ""
                      }`}
                    </option>
                  ),
                )}
              </CustomSelectDropdown>
            </div>

            {/* Mandate Type Dropdown */}
            <div style={{ flex: "1 1 48%" }}>
              <CustomSelectDropdown
                label="Select Mandate Type"
                extraLabel={isLoading ? "loading..." : ""}
                onChange={(e) => setMandateTypeId(e.target.value)}
                name="mandateTypes"
                className="round user__form form-control"
              >
                <option value="">Select Mandate Type</option>
                {renderMandateTypeOptions()}
              </CustomSelectDropdown>
            </div>
          </div>

          <div>
            <button
              type="button"
              data-toggle="modal"
              data-target="#directDebitMandateSubmit"
              className="btn zoom-element btn login-btn"
              disabled={isCreateMandateDisabled}
              onClick={createMandatePreview}
            >
              {mandatePreviewLoading ? "Loading..." : "Create Mandate"}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.directDebitDropdownSelector}>
          <div style={{ flex: "1 1 48%" }}>
            <CustomSelectDropdown
              label="Select Direct Debit Account"
              extraLabel={DirectDebitListAccountsLoading ? "loading..." : ""}
              onChange={(e) => handleRadioChange(e.target.value)}
              name="directDebitAccounts"
              className="round user__form form-control"
            >
              <option value="">All Direct Debit Accounts</option>
              {directDebitListAccountsData?.bank_accounts?.map(
                ({
                  account_number,
                  account_name,
                  bank_name,
                  is_default,
                }: {
                  account_number: string;
                  account_name: string;
                  bank_name: string;
                  is_default: boolean;
                }) => (
                  <option value={account_number} key={account_number}>
                    {`${bank_name} - ${account_number} - ${account_name} ${
                      is_default ? "(Selected by customer)" : ""
                    }`}
                  </option>
                ),
              )}
            </CustomSelectDropdown>
          </div>
          <div>
            <button
              type="button"
              data-toggle="modal"
              data-target="#directDebitMandateSubmit"
              className={`${styles.createMandateBtn} btn zoom-element btn login-btn`}
              disabled={isCreateMandateDisabled}
              onClick={createMandatePreview}
            >
              Create Mandate
            </button>
          </div>
        </div>
      )}

      <div style={{ width: "100%", marginTop: "20px" }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={directDebitMandateTableList || []}
          loading={
            directDebitMandateTableListLoading || directDebitQueryLoading
          }
          bordered
          pagination={false}
          locale={{ emptyText: "No Mandate has been created for this account" }}
        />
      </div>

      <DirectDebitMandateModalPrompt
        setError={setError}
        setSuccess={setSuccess}
        mandatePreviewLoading={mandatePreviewLoading}
        mandatePreviewError={mandatePreviewError}
        mandatePreviewSuccess={mandatePreviewSuccess}
        loan_id={loan_id}
        loan_ref={loan_ref}
        setCurrentBankAccount={setCurrentBankAccount}
        currentBankAccount={currentBankAccount}
        scrollToTop={scrollToTop}
        mandatePreviewResponseData={mandatePreviewResponseData}
        mandateTypeId={mandateTypeId}
        isBusinessApplication={
          properties?.dataRecord?.application_type?.toLowerCase()?.trim() ===
          "spark"
        }
      />

      <MandatePayload
        requestPayLoad={requestPayLoad}
        responsePayLoad={responsePayLoad}
      />

      <ViewMandateTemplate
        showMandateTemplateModal={showMandateTemplateModal}
        setShowMandateTemplateModal={setShowMandateTemplateModal}
        mandateTemplate={mandateTemplate}
        handleDownload={handleDownload}
        isDownloading={isDownloading}
      />

      {resendMandateStatus?.error ? (
        <Alert message={`${resendMandateStatus.message}`} />
      ) : (
        resendMandateStatus?.status === "success" && (
          <Alert type="success" message={`${resendMandateStatus.message}`} />
        )
      )}

      <ConfirmMandateApproval
        showConfirmMandateApprovalModal={showConfirmMandateApprovalModal}
        setShowConfirmMandateApprovalModal={setShowConfirmMandateApprovalModal}
        isLoadingApproval={isLoadingApproval}
        onApproveMandate={onApproveMandate}
        approveMandateErrorMsg={approveMandateErrorMsg}
        setApproveMandateErrorMsg={setApproveMandateErrorMsg}
      />

      <RejectMandate
        showRejectModal={showRejectModal}
        setShowRejectModal={setShowRejectModal}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        rejectLoading={rejectLoading}
        selectedMandateItem={selectedMandateItem}
        handleMandateRejection={handleMandateRejection}
        rejectErrorMsg={rejectReasonErrorMsg}
      />

      <CustomerPaperMandate
        showCustomerMandateModal={showCustomerMandateModal}
        setShowCustomerMandateModal={setShowCustomerMandateModal}
        isFetchingDocument={isFetchingDocument}
        setCustomerMandateUrl={setCustomerMandateUrl}
        handleDownload={handleDownload}
        isDownloading={isDownloading}
      />
    </div>
  );
};

export default DirectDebit;
